import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { catchError, first, switchMap, finalize } from 'rxjs/operators';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ConfirmationDialogComponent } from '@app/shared/dialog/confirmation-dialog/confirmation-dialog.component';
import { AuthService } from '@app/shared/services/auth.service';
import { Region, RegionService } from '@app/shared/services/api/regions.service';
import { Municipality, MunicipalityService } from '@app/shared/services/api/municipality.service';
import { environment } from '@env/environment';
import { StringUtility } from '@app/shared/string-utility';
import { UserRegistration } from './../model/user-registration';
import { UserService } from '@app/shared/services/api/user.service';

@Injectable()
export class UserRegistrationRequestService {
  public municipalities$: BehaviorSubject<Municipality[]> = new BehaviorSubject<Municipality[]>([]);
  public isMunicipalitiesLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public regions$: BehaviorSubject<Region[]> = new BehaviorSubject<Region[]>([]);
  public isRegionsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isSubmittingRegistration$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly municipalityService: MunicipalityService,
    private readonly regionService: RegionService,
    private readonly userService: UserService
  ) {
    this.isMunicipalitiesLoading$.next(true);
    this.isRegionsLoading$.next(true);
    this.createSubscriptions();
  }

  public submitRegistration(userRegistrationRequestForm: FormGroup, selectedMunicipalityGuid: string): void {
    this.isSubmittingRegistration$.next(true);
    const registrationData: UserRegistration = this.getRegistrationData(userRegistrationRequestForm);
    const isMunicipalityWorker = environment.registrationRequestRoles[0] === registrationData.Role;

    this.userService
      .submitRegistrationRequest(registrationData, selectedMunicipalityGuid, isMunicipalityWorker)
      .pipe(
        first(),
        catchError((e) => {
          this.showConfirmDialog('warning');
          return throwError(() => e);
        }),
        finalize(() => {
          this.isSubmittingRegistration$.next(false);
          userRegistrationRequestForm.enable();
        })
      )
      .subscribe(() => {
        this.showConfirmDialog('success');
      });
  }

  private showConfirmDialog(status: string): void {
    const isSuccessful = status === 'success';
    const confirmDialog = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(
          `module.public.registrationRequest.${isSuccessful ? 'requestRegistrationConfirmTitle' : 'requestRegistrationErrorTitle'}`
        ),
        status,
        description: this.translate.instant(
          `module.public.registrationRequest.${isSuccessful ? 'requestRegistrationConfirmMessage' : 'requestRegistrationErrorMessage'}`
        ),
        confirmText: isSuccessful ? this.translate.instant('module.public.registrationRequest.requestRegistrationConfirmButton') : null,
      },
      position: { top: '160px' },
      panelClass: ['ep-generic-dialog'],
    });
    if (isSuccessful) {
      confirmDialog.afterClosed().subscribe(() => this.authService.login());
    }
  }

  protected getRegistrationData(userRegistrationRequestForm: FormGroup): UserRegistration {
    const municipalitySelected: Municipality | Region = userRegistrationRequestForm.get('municipality')?.value;
    const requestorEmail = userRegistrationRequestForm.get('email')?.value;

    return {
      LastName: userRegistrationRequestForm.get('lastName')?.value,
      FirstName: userRegistrationRequestForm.get('firstName')?.value,
      Role: userRegistrationRequestForm.get('role')?.value,
      Municipality: (municipalitySelected as Municipality).municipalityKey
        ? StringUtility.getLocationName(municipalitySelected)
        : (municipalitySelected.name ?? ''),
      Email: requestorEmail,
      Telephone: userRegistrationRequestForm.get('telephone')?.value,
      Comment: userRegistrationRequestForm.get('comment')?.value === '' ? 'NA' : userRegistrationRequestForm.get('comment')?.value,
      FullName: userRegistrationRequestForm.get('firstName')?.value + ' ' + userRegistrationRequestForm.get('lastName')?.value,
    };
  }

  private createSubscriptions(): void {
    this.municipalityService
      .getAllConcise()
      .pipe(
        first(),
        switchMap((municipalities: Municipality[]) => of([...municipalities].sort((a, b) => a.name.localeCompare(b.name))))
      )
      .subscribe((municipalities: Municipality[]) => {
        this.isMunicipalitiesLoading$.next(false);
        this.municipalities$.next(municipalities);
      });

    this.regionService
      .getByRegionType('AM')
      .pipe(
        first(),
        switchMap((regions: Region[]) => of([...regions].sort((a, b) => a.name.localeCompare(b.name))))
      )
      .subscribe((regions: Region[]) => {
        this.isRegionsLoading$.next(false);
        this.regions$.next(regions);
      });
  }
}
